import React, {useCallback, useEffect, useState} from "react";
import {collection, doc, getDoc, getDocs, query, where} from "@firebase/firestore";
import {Link, useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {db} from "../../../firebase/FirebaseProvider";
import {Location, Session, Stay, User, SettingsDoc} from "../../types/Types";
import LocationDetailsV2 from "./LocationDetailsV2";
import UserTag from "../../users/tag/UserTag";
import DateTime from "../../date/DateTime";
import PageTracking from "../../auth/PageTracking";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import "./LocationDetailsV2.css";

interface Props {
    currentUser: User | undefined;
    usersMap: Map<string, User> | undefined;
    sessionsMap: Map<string, Session>;
    settings: SettingsDoc | undefined;
    likesMap: Map<string, boolean>;
}

function getStayTimestamp(value: any): number {
    if (value?.seconds) {
        return value.seconds * 1000;
    }
    if (value?.toDate) {
        return value.toDate().getTime();
    }
    return new Date(value).getTime();
}

function LocationStays(props: Props) {
    const {id} = useParams<{id: string}>();
    const [location, setLocation] = useState<Location | undefined>(undefined);
    const [stays, setStays] = useState<Array<Stay> | undefined>(undefined);

    const isUserSocial = useCallback((userId: string): boolean => {
        const user = props.usersMap?.get(userId);
        return !!user && !user.socialDisabled;
    }, [props.usersMap]);

    useEffect(() => {
        if (!id) {
            return;
        }

        (async () => {
            const locationSnapshot = await getDoc(doc(db, "locations", id));
            if (locationSnapshot.exists()) {
                setLocation({...locationSnapshot.data() as Location, id: locationSnapshot.id});
            }
        })().catch((error) => console.error("Failed to load stay location", error));
    }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }

        (async () => {
            const staySnapshot = await getDocs(query(
                collection(db, "stays"),
                where("locationId", "==", id),
                where("stayed", "==", true),
            ));
            const loadedStays = staySnapshot.docs
                .map((stayDoc) => stayDoc.data() as Stay)
                .filter((stay) => isUserSocial(stay.userId))
                .sort((first, second) => getStayTimestamp(second.lastModified) - getStayTimestamp(first.lastModified));
            setStays(loadedStays);
        })().catch((error) => {
            console.error("Failed to load location stays", error);
            setStays([]);
        });
    }, [id, isUserSocial]);

    const getUser = (userId: string): User => props.usersMap?.get(userId) || {} as User;

    return (
        <>
            <PageTracking />
            {props.currentUser && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                variant="header"
            />}
            {location && <V2GlassPanel className="location-v2-detail-toolbar">
                <Link className="location-v2-back-button" to={`/location/${location.id}/comments`}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Pub
                </Link>
                <div className="location-v2-detail-stats">
                    <V2GlassSurface className="location-v2-detail-stat">
                        <span>Stays</span>
                        <b>{location.stayCount || stays?.length || 0}</b>
                    </V2GlassSurface>
                </div>
            </V2GlassPanel>}
            <V2GlassPanel className="location-v2-detail-list-card v2-glass-list-panel">
                {stays && props.usersMap && stays.map((stay, index) => (
                    <V2GlassSurface key={`${stay.userId}-${index}`} className="location-v2-visit-row v2-glass-list-row">
                        <UserTag
                            user={getUser(stay.userId)}
                            session={props.sessionsMap.get(stay.userId)}
                            visit={undefined}
                            padRight={true}
                            hideLocationDot={false}
                            linksToTab="stays"
                        />
                        <V2GlassChip className="location-v2-visit-date-chip">
                            <DateTime date={stay.lastModified} />
                        </V2GlassChip>
                    </V2GlassSurface>
                ))}
                {stays && stays.length === 0 && <div className="location-v2-empty-list">No stays yet</div>}
                {!stays && <div className="location-v2-empty-list">Loading...</div>}
            </V2GlassPanel>
        </>
    );
}

export default LocationStays;
